import { Alignment, Button, Classes, Dialog, FormGroup, H5, InputGroup, Popover, Switch, Tooltip } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { observer } from "mobx-react";
import * as moment from "moment";
import * as React from "react";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { ICalc, IEmpresa, IMemoriaCalculo, newIMemoria } from "../../model";
import { AppStore } from "../../stores/AppStore";
import { MemoriaStore } from "../../stores/memoria/MemoriaStore";
import { toast } from "react-toastify";
import { formatCnpj, formatCpf } from "../../utils";
import { Pager } from "../utils/Pager";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { CalcRSStore } from "../../stores/calculo-rs/CalcRSStore";
import { CalcStore } from "../../stores/calculo/CalcStore";
import CnpjCpfInput from "../utils/CnpjCpfInput";
import LoadingModal from "../loading/LoadingModal";
import { CalcSelect } from "../calculos/CalcSelect";
import { filterCalcs, renderCalcs } from "../utils/CalcSelect";
import { GenerateCalcOptions } from "../contestar/calcs_options";
import { AppIcon } from "../utils/AppIcon";
import { MemoriaConfig } from "./memoriaConfigs";

var debounce = require('lodash.debounce');

const BrV = require("br-validations");

/**
 * Props de {@link MemoriaBase}.
 *
 * @property config Configuração específica do modelo fiscal que define filtros,
 *                  colunas, campos do formulário e validações.
 */
interface MemoriaBaseProps {
  config: MemoriaConfig;
}

/**
 * Componente genérico de gerenciamento de memórias de cálculo.
 *
 * Centraliza toda a lógica de listagem, criação, edição, exclusão e exportação
 * de memórias de cálculo, sendo parametrizado por uma {@link MemoriaConfig} para
 * atender a cada modelo fiscal (NF-e, NFC-e, CT-e, NFS-e) sem duplicação de código.
 *
 * O estado é mantido em {@link MemoriaStore} e o componente é reativo via MobX
 * (decorador `@observer`).
 */
@observer
export class MemoriaBase extends React.Component<MemoriaBaseProps, {}> {

  /** Versão com debounce (800ms) de {@link handleMemorias}, usada nos filtros de texto. */
  fetchOptions: any;

  constructor(props) {
    super(props);
    this.fetchOptions = debounce(this.handleMemorias.bind(this), 800);
  }

  /** Modelo fiscal corrente, extraído da configuração recebida via props. */
  get modelo() {
    return this.props.config.modelo;
  }

  /**
   * Inicializa o modelo no store e, havendo empresa selecionada, dispara a
   * busca inicial das memórias.
   */
  componentDidMount() {
    MemoriaStore.obs.modelo = this.modelo;

    if (MemoriaStore.obs.select_empresa.cnpj) {
      this.handleMemorias();
    }
  }

  /**
   * Exclui uma única memória a partir da linha da tabela.
   *
   * Limpa a seleção atual, marca apenas a linha informada e dispara a exclusão.
   *
   * @param row Linha da memória a ser excluída.
   */
  async handleDeleteMemoria(row: any) {
    MemoriaStore.resetDeletarItems()
    this.onSelect(row, true)
    this.handleDelete()
  }

  /**
   * Manipula a alteração dos seletores principais (empresa, tipo, estado).
   *
   * Reinicia a paginação, recarrega as memórias e os cálculos válidos do estado
   * correspondente (RS ou demais).
   *
   * @param e Valor selecionado (atribuído ao store antes da chamada).
   */
  async handleBuscaMemoria(e) {

    MemoriaStore.obs.current_page = 1

    this.handleMemorias()

    MemoriaStore.resetDeletarItems()

    if (MemoriaStore.obs.estado == "RS") {

      CalcRSStore.load();

    } else {

      CalcStore.load();

    }

  }

  /**
   * Busca as memórias de cálculo da empresa selecionada, aplicando os filtros,
   * modelo, estado, tipo e página correntes.
   *
   * Exibe um aviso e aborta caso nenhuma empresa esteja selecionada.
   */
  async handleMemorias() {

    if (!MemoriaStore.obs.select_empresa.cnpj) {
      toast.warn('Selecione uma Empresa.', {});
      return
    }

    let data = {
      cnpj: MemoriaStore.obs.select_empresa.cnpj,
      page: MemoriaStore.obs.current_page,
      limit: 50,
      modelo: this.modelo,
      filter: MemoriaStore.obs.filters,
      estado: MemoriaStore.obs.estado,
      tipo: MemoriaStore.obs.entrada_saida
    }

    await MemoriaStore.handleMemorias(data)

    MemoriaStore.resetDeletarItems()

  }

  /**
   * Navega para uma página específica da listagem e recarrega as memórias.
   *
   * @param page Número da página a ser exibida.
   */
  async handleChangeBuscaMemoria(page: number) {
    MemoriaStore.obs.current_page = page
    this.handleMemorias()
    MemoriaStore.resetDeletarItems()
  }

  /**
   * Manipula a seleção/desseleção de todas as linhas (checkbox do cabeçalho).
   *
   * @param isSelect Indica se as linhas foram selecionadas.
   * @param rows     Linhas afetadas pela ação.
   */
  onSelectAll = (isSelect, rows) => {
    if (isSelect) {
      MemoriaStore.obs.deletarMemorias = rows.map(x => x.id);
    } else {
      MemoriaStore.resetDeletarItems();
    }
  };

  /**
   * Manipula a seleção/desseleção de uma única linha, mantendo a lista de
   * memórias marcadas para exclusão.
   *
   * @param row     Linha afetada.
   * @param checked Indica se a linha foi marcada.
   */
  onSelect = (row, checked) => {
    if (checked) {
      MemoriaStore.obs.deletarMemorias.push(row.id);
    } else {
      MemoriaStore.obs.deletarMemorias = MemoriaStore.obs.deletarMemorias.filter(value => value != row.id)
    }
  };

  /**
   * Valida e persiste a memória em edição (criação ou atualização).
   *
   * Define os CNPJs de origem/destino conforme o tipo (entrada/saída), valida
   * o cálculo selecionado e os campos obrigatórios da configuração, e ao final
   * recarrega a listagem e limpa o formulário.
   */
  async handleSaveMemoria() {

    let memoria: any = MemoriaStore.obs.create_memoria

    if (!MemoriaStore.obs.calc_selecionado || !MemoriaStore.obs.calc_selecionado._id) {
      toast.warning('Por favor, selecione um Cálculo.', {});
      return;
    }

    if (MemoriaStore.obs.entrada_saida == "entrada") {

      if (!memoria.cnpj_origem && memoria.cnpj_memoria) {
        toast.warning('Por favor, digite um CNPJ.', {});
        return;
      }

      memoria.cnpj_origem = memoria.cnpj_memoria ? memoria.cnpj_origem : ""
      memoria.cnpj_destino = MemoriaStore.obs.select_empresa.cnpj

    } else {

      if (!memoria.cnpj_destino && memoria.cnpj_memoria) {
        toast.warning('Por favor, digite um CNPJ.', {});
        return;
      }

      memoria.cnpj_destino = memoria.cnpj_memoria ? memoria.cnpj_destino : ""
      memoria.cnpj_origem = MemoriaStore.obs.select_empresa.cnpj

    }

    for (let regra of this.props.config.required) {
      if (!memoria[regra.key]) {
        toast.warning(regra.message, {});
        return;
      }
    }

    if (!memoria.cest) {
      memoria.cest = null
    }

    memoria.modelo = this.modelo
    memoria.estado = MemoriaStore.obs.estado
    memoria.mva = memoria.mva.toString()
    memoria.calc_id = MemoriaStore.obs.calc_selecionado._id

    MemoriaStore.obs.open_create = false

    let resp = false

    if (memoria.id) {
      resp = await MemoriaStore.handleUpdateMemoria(memoria)
    } else {
      resp = await MemoriaStore.handleCreateMemoria(memoria)
    }

    if (resp) {
      MemoriaStore.obs.current_page = 1
      this.handleMemorias()
    }

    MemoriaStore.obs.create_memoria = newIMemoria()
    MemoriaStore.obs.calc_selecionado = null

  }

  /**
   * Carrega as opções de cálculo disponíveis para o modal de criação/edição,
   * filtradas por tipo, modelo e empresa, a partir do store de cálculos do
   * estado corrente (RS ou demais).
   */
  async loadCalcOptions() {

    let tmp_tipo = MemoriaStore.obs.entrada_saida

    if (MemoriaStore.obs.estado == "RS") {

      MemoriaStore.obs.calcOptions = GenerateCalcOptions(CalcRSStore.validForDate(), tmp_tipo, this.modelo, MemoriaStore.obs.select_empresa)

    } else {

      MemoriaStore.obs.calcOptions = GenerateCalcOptions(CalcStore.validForDate(), tmp_tipo, this.modelo, MemoriaStore.obs.select_empresa)

    }

  }

  /** Fecha o modal de criação/edição e limpa a memória em edição e o cálculo selecionado. */
  onClose() {

    MemoriaStore.obs.open_create = false

    MemoriaStore.obs.create_memoria = newIMemoria()
    MemoriaStore.obs.calc_selecionado = null

  }

  /**
   * Exclui as memórias atualmente marcadas e, em caso de sucesso, retorna à
   * primeira página e recarrega a listagem.
   */
  async handleDelete() {
    let resp = await MemoriaStore.handleDeleteMemorias()

    if (resp) {
      MemoriaStore.obs.current_page = 1
      this.handleMemorias()
    }

    MemoriaStore.resetDeletarItems()
  }

  /** Abre o modal de criação de memória, carregando previamente as opções de cálculo. */
  openCreateMemoria = async () => {

    this.loadCalcOptions()

    MemoriaStore.obs.open_create = true

  }

  /**
   * Abre o modal em modo de edição, populando o formulário com os dados da
   * linha e restaurando o cálculo associado.
   *
   * @param row Linha da memória a ser editada.
   */
  handleEditMemoria(row) {

    this.loadCalcOptions()

    row.mva = row.mva ? parseFloat(row.mva) : ""

    MemoriaStore.obs.create_memoria = row

    if (MemoriaStore.obs.estado == "RS") {

      MemoriaStore.obs.calc_selecionado = CalcRSStore.obs.calcs.find(x => x._id == row.calc_id)

    } else {

      MemoriaStore.obs.calc_selecionado = CalcStore.obs.calcs.find(x => x._id == row.calc_id)

    }

    MemoriaStore.obs.open_create = true

  }

  /**
   * Manipula a digitação nos filtros de texto, reiniciando a paginação e
   * disparando a busca com debounce ({@link fetchOptions}).
   *
   * @param e Valor do filtro (atribuído ao store antes da chamada).
   */
  handleBuscaMemorias(e: any) {
    MemoriaStore.obs.current_page = 1
    this.fetchOptions()
  }

  /** Limpa todos os filtros, retorna à primeira página e recarrega a listagem. */
  hanldeClearFiltros() {
    MemoriaStore.obs.current_page = 1
    MemoriaStore.resetFilters()
    this.handleMemorias();
  }

  /**
   * Exporta as memórias para Excel respeitando os filtros atuais.
   *
   * Normaliza os CNPJs dos filtros (removendo caracteres não numéricos) e, em
   * caso de sucesso, alterna para a aba de relatório.
   */
  async handleExport() {

    MemoriaStore.obs.filters.cnpj_origem = MemoriaStore.obs.filters.cnpj_origem.replace(/\D/g, "")

    MemoriaStore.obs.filters.cnpj_destino = MemoriaStore.obs.filters.cnpj_destino.replace(/\D/g, "")

    let data = {
      cnpj: MemoriaStore.obs.select_empresa.cnpj,
      razao_social: MemoriaStore.obs.select_empresa.razao_social,
      modelo: this.modelo,
      filter: MemoriaStore.obs.filters,
      estado: MemoriaStore.obs.estado,
      tipo: MemoriaStore.obs.entrada_saida
    }

    let resp = await MemoriaStore.handleExport(data)

    if (resp) MemoriaStore.obs.selectedTab = "relatorio_memoria"

  }

  /**
   * Renderiza o filtro de CNPJ, alternando entre "CNPJ Origem" e "CNPJ Destino"
   * conforme o tipo de operação (entrada/saída).
   */
  renderFiltroCnpj() {
    let filters: any = MemoriaStore.obs.filters
    return MemoriaStore.obs.entrada_saida == "entrada" ?
      <MDBCol size="6" md="3" xl="2" key="filtro-cnpj">
        <FormGroup label="CNPJ Origem">
          <CnpjCpfInput
            onChange={(val) => this.handleBuscaMemorias(filters.cnpj_origem = val)}
            value={filters.cnpj_origem}
            disabled={false}
          />
        </FormGroup>
      </MDBCol>
      :
      <MDBCol size="6" md="3" xl="2" key="filtro-cnpj">
        <FormGroup label="CNPJ Destino">
          <CnpjCpfInput
            onChange={(val) => this.handleBuscaMemorias(filters.cnpj_destino = val)}
            value={filters.cnpj_destino}
            disabled={false}
          />
        </FormGroup>
      </MDBCol>
  }

  /**
   * Renderiza os filtros definidos na configuração, delegando o filtro de CNPJ
   * para {@link renderFiltroCnpj} e renderizando os demais como campos de texto.
   */
  renderFiltros() {
    let filters: any = MemoriaStore.obs.filters
    return this.props.config.filters.map((filtro, idx) => {
      if (filtro.kind == "cnpj") {
        return this.renderFiltroCnpj()
      }
      return (
        <MDBCol {...filtro.colProps} key={`filtro-${filtro.filterKey}-${idx}`}>
          <FormGroup label={filtro.label}>
            <InputGroup
              style={{ borderRadius: "10px" }}
              placeholder={filtro.placeholder || `Filtrar (${filtro.label})`}
              value={filters[filtro.filterKey]}
              onChange={(e) => this.handleBuscaMemorias(filters[filtro.filterKey] = e.target.value)}
            />
          </FormGroup>
        </MDBCol>
      )
    })
  }

  /**
   * Renderiza uma linha de campos do formulário do modal.
   *
   * A primeira linha inclui o campo de CNPJ da memória ({@link renderCnpjMemoriaField}).
   *
   * @param row      Campos da linha (vide {@link MemoriaConfig.formRows}).
   * @param isFirst  Indica se é a primeira linha do formulário.
   * @param rowIndex Índice da linha, usado como chave de renderização.
   */
  renderFormRow(row, isFirst: boolean, rowIndex: number) {
    let create: any = MemoriaStore.obs.create_memoria

    let nodes: any[] = isFirst ? [this.renderCnpjMemoriaField()] : []

    row.forEach((campo) => {
      nodes.push(
        <FormGroup
          key={`campo-${campo.key}`}
          className="form-group"
          label={campo.label}
          labelInfo={campo.required ? "*" : undefined}
          style={{ width: campo.width }}
        >
          <InputGroup type="text" value={create[campo.key]} onChange={(e) => create[campo.key] = e.target.value} />
        </FormGroup>
      )
    })

    return (
      <div style={{ display: "flex" }} key={`row-${rowIndex}`}>
        {nodes.map((node, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <>&nbsp;</> : null}
            {node}
          </React.Fragment>
        ))}
      </div>
    )
  }

  /**
   * Renderiza o campo de CNPJ da memória (origem ou destino conforme o tipo),
   * exibido apenas quando a opção "Memória CNPJ" está habilitada.
   */
  renderCnpjMemoriaField() {
    let create: any = MemoriaStore.obs.create_memoria
    return (
      <React.Fragment key="cnpj-memoria-field">
        {create.cnpj_memoria ?
          <>
            {MemoriaStore.obs.entrada_saida == "entrada" ?
              <FormGroup label={"CNPJ Origem"} style={{ width: "50%" }} labelInfo="*">
                <CnpjCpfInput
                  onChange={val => create.cnpj_origem = val}
                  value={create.cnpj_origem}
                  disabled={false}
                />
              </FormGroup>
              :
              <FormGroup label={"CNPJ Destino"} style={{ width: "50%" }} labelInfo="*">
                <CnpjCpfInput
                  onChange={val => create.cnpj_destino = val}
                  value={create.cnpj_destino}
                  disabled={false}
                />
              </FormGroup>
            }
          </>
          : <></>}
      </React.Fragment>
    )
  }

  /**
   * Renderiza a tela completa: seletores principais, filtros, ações em massa,
   * tabela paginada de memórias e o modal de criação/edição.
   */
  render() {

    const config = this.props.config

    let niveis_acesso = AppStore.obs1.usuario ? AppStore.obs1.usuario.niveis_acesso : [""];
    let userPrincipal = AppStore.obs1.usuario ? AppStore.obs1.usuario.principal : false;

    function cnpjFormatter(cell, row) {
      if (BrV.cnpj.validate(cell)) {
        return formatCnpj(cell)
      } else if (BrV.cpf.validate(cell)) {
        return formatCpf(cell)
      }
      return cell
    }

    const buttonFormatter = (cell: string, row: IMemoriaCalculo) => {
      return (
        <>
          {userPrincipal || niveis_acesso.indexOf("c4") == 1
            ? <>
              <MDBBtn color="default" rounded size="sm" onClick={(e) => this.handleEditMemoria(row)}>
                <img src="/images/edit.png" width={25} />
              </MDBBtn>
              <Popover popoverClassName={Classes.POPOVER_CONTENT_SIZING}>
                <MDBBtn color="default" rounded size="sm">
                  <img src="/images/block.png" width={25} />
                </MDBBtn>
                <div>
                  <H5>Excluir memória?</H5>
                  <p>Você confirma excluir esta memória: {row.calc_nome}?</p>
                  <p>
                    Ao excluir essa memória o sistema não aplicará mais o
                    cálculo automaticamente.
                  </p>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15 }}>
                    <Button className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }}>
                      Cancelar
                    </Button>
                    <Button intent="danger" className={Classes.POPOVER_DISMISS} onClick={(e) => this.handleDeleteMemoria(row)}>
                      Excluir
                    </Button>
                  </div>
                </div>
              </Popover>
            </>
            : <></>
          }
        </>
      );
    };

    let items = MemoriaStore.obs.memoria_calculo?.map((memoria) => {
      return ({
        id: memoria._id,
        calc_nome: memoria.calc_nome,
        cnpj_destino: memoria.cnpj_destino,
        cnpj_origem: memoria.cnpj_origem,
        inserted_at: moment(memoria._insertedTS || memoria.inserted_at).add(3, "hours").format("DD/MM/YYYY") + " " + moment(memoria._insertedTS || memoria.inserted_at).add(3, "hours").format("HH:mm"),
        mva: memoria.mva,
        calc_id: memoria.calc_id,
        cnpj_memoria: memoria.cnpj_memoria,
        ...config.mapItem(memoria),
      });
    });

    const selectRow = {
      mode: "checkbox",
      clickToSelect: false,
      columnWidth: "20px",
      onSelectAll: this.onSelectAll,
      onSelect: this.onSelect,
    };

    let empresaText = MemoriaStore.obs.select_empresa.razao_social != ""
      ? `${MemoriaStore.obs.select_empresa.razao_social.substring(0, 60)}... (${formatCnpj(MemoriaStore.obs.select_empresa.cnpj)})`
      : "Selecione uma empresa";

    let calcText = !MemoriaStore.obs.calc_selecionado?.nome
      ? "Selecione o Cálculo"
      : `${MemoriaStore.obs.calc_selecionado.nome.length >= 100
        ? MemoriaStore.obs.calc_selecionado.nome.substring(0, 100) + "..."
        : MemoriaStore.obs.calc_selecionado.nome}`

    return (
      <>

        <MDBRow>

          <MDBCol size="12" md="6" xl="4">
            <FormGroup className="empresaSelect" label={MemoriaStore.obs.entrada_saida == "entrada" ? "Empresa Destino" : "Empresa Origem"} labelInfo="*">
              <EmpresaSelect
                items={EmpresaStore.sortedEmpresas}
                itemRenderer={renderEmpresa}
                itemPredicate={filterEmpresa}
                onItemSelect={(e: IEmpresa) => {
                  if (config.onEmpresaSelect) config.onEmpresaSelect(e);
                  this.handleBuscaMemoria(MemoriaStore.obs.select_empresa = e)
                }}
                inputProps={{ placeholder: "Filtrar uma empresa" }}
              >
                <Button
                  text={empresaText}
                  rightIcon="double-caret-vertical"
                  alignText={Alignment.LEFT}
                />
              </EmpresaSelect>
            </FormGroup>
          </MDBCol>

          <MDBCol size="6" md="3" xl="2">
            <FormGroup label="Tipo" className="selectFullWidth" labelInfo="*">
              <select
                onChange={(e) => this.handleBuscaMemoria(MemoriaStore.obs.entrada_saida = e.target.value)}
                value={MemoriaStore.obs.entrada_saida}
                className="bp3-button bp3-align-left"
              >
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
            </FormGroup>
          </MDBCol>

          <MDBCol size="6" md="3" xl="2">
            <FormGroup label="Estado" className="selectFullWidth" labelInfo="*">
              <select
                onChange={(e) => this.handleBuscaMemoria(MemoriaStore.obs.estado = e.target.value)}
                value={MemoriaStore.obs.estado}
                className="bp3-button bp3-align-left"
              >
                <option value="PA">PA</option>
                <option value="RS">RS</option>
              </select>
            </FormGroup>
          </MDBCol>

          {this.renderFiltros()}

        </MDBRow>

        <div style={{ display: "flex", float: 'left', gap: 10 }}>
          <MDBBtn className="btnDefault btnBuscarNfe btnClearFilters" onClick={() => this.hanldeClearFiltros()}>
            Limpar Filtros
          </MDBBtn>
          {MemoriaStore.obs.select_empresa.cnpj &&
            <MDBBtn className="btnDefault btnBuscarNfe" onClick={() => this.openCreateMemoria()}>Criar Memória</MDBBtn>
          }
        </div>

        <div style={{ display: "flex", float: 'right', gap: 5 }}>
          {MemoriaStore.obs.select_empresa.cnpj &&
            <MDBBtn className="btnDefault btnBuscarNfe" onClick={() => this.handleExport()}>Exportar Excel</MDBBtn>
          }
          <Popover popoverClassName={Classes.POPOVER_CONTENT_SIZING}>
            <Tooltip
              content={`Deletar ${MemoriaStore.obs.deletarMemorias.length}`}
              disabled={MemoriaStore.obs.deletarMemorias.length == 0}
            >
              <Button disabled={MemoriaStore.obs.deletarMemorias.length == 0} icon="issue">
                Deletar Selecionados
              </Button>
            </Tooltip>
            <div>
              <H5>Confirmar exclusão</H5>
              <p style={{ fontSize: 11 }}>Tem certeza de que deseja excluir esse item? Você não será capaz de recuperá-lo.</p>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15, }}>
                <Button className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }} onClick={() => MemoriaStore.resetDeletarItems()}>
                  Cancelar
                </Button>
                <Button intent="danger" className={Classes.POPOVER_DISMISS} onClick={() => this.handleDelete()}>
                  Deletar
                </Button>
              </div>
            </div>
          </Popover>
        </div>


        <div style={{ marginTop: 50 }}>
          <BootstrapTable className="TableDefault" ref="table" data={items} selectRow={selectRow}>
            <TableHeaderColumn dataField="id" isKey={true} hidden>#</TableHeaderColumn>
            <TableHeaderColumn width={"150px"} dataField="calc_nome">Nome do Cálculo</TableHeaderColumn>
            {(config.columnsBefore || []).map((col) => (
              <TableHeaderColumn key={col.dataField} width={col.width} dataField={col.dataField}>{col.label}</TableHeaderColumn>
            ))}
            <TableHeaderColumn width={"80px"} dataFormat={cnpjFormatter} dataField="cnpj_destino">CNPJ Destino</TableHeaderColumn>
            <TableHeaderColumn width={"80px"} dataFormat={cnpjFormatter} dataField="cnpj_origem">CNPJ Origem</TableHeaderColumn>
            {config.columns.map((col) => (
              <TableHeaderColumn key={col.dataField} width={col.width} dataField={col.dataField}>{col.label}</TableHeaderColumn>
            ))}
            <TableHeaderColumn width={"80px"} dataField="inserted_at">Data do Cadastro</TableHeaderColumn>
            <TableHeaderColumn width={"50px"} dataFormat={buttonFormatter}>Ações</TableHeaderColumn>
          </BootstrapTable>
        </div>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
          <Pager
            currentPage={MemoriaStore.obs.current_page}
            totalCount={MemoriaStore.obs.memoria_length}
            pageSize={50}
            onPageChange={page => this.handleChangeBuscaMemoria(page)} />
        </div>

        <LoadingModal open={MemoriaStore.obs.loading} onClose={() => MemoriaStore.obs.loading = false} title={"Buscando memórias de cálculo"} />
        <LoadingModal open={MemoriaStore.obs.loading_delete} onClose={() => MemoriaStore.obs.loading_delete = false} title={"Deletando memórias"} />

        <Dialog
          isOpen={MemoriaStore.obs.open_create}
          onClose={() => this.onClose()}
          className="AlertUser"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="titleSEFAZ" style={{ margin: 0 }}>{MemoriaStore.obs.create_memoria.id ? "Editando Memória" : "Criar Memória"}</h2>
            <div style={{ display: "flex", alignItems: "center" }}>
              <label style={{ marginBottom: 0 }}>
                <b>Memória CNPJ</b>
                <AppIcon
                  name="help"
                  className="pulse-notification-pgdas"
                  color="blue"
                  style={{ cursor: "pointer", marginLeft: 5 }}
                  onClick={() => MemoriaStore.obs.open_ajuda = true}
                />
              </label>
              <Switch
                style={{ paddingTop: 8 }}
                alignIndicator={Alignment.RIGHT}
                innerLabelChecked="Sim"
                innerLabel="Não"
                checked={MemoriaStore.obs.create_memoria.cnpj_memoria}
                onChange={(e: any) => MemoriaStore.obs.create_memoria.cnpj_memoria = e.target.checked}
              />
            </div>
          </div>

          <div style={{ display: "flex" }}>
            <FormGroup className="empresaSelect" label="Cálculo" labelInfo="*" style={{ width: "100%" }}>
              <CalcSelect
                items={MemoriaStore.obs.calcOptions}
                itemRenderer={renderCalcs}
                itemPredicate={filterCalcs}
                onItemSelect={(e: ICalc) => {
                  MemoriaStore.obs.calc_selecionado = e
                }}
                inputProps={{ placeholder: "Filtrar um Cálculo" }}
                matchTargetWidth
              >
                <Button text={calcText} rightIcon="double-caret-vertical" alignText={Alignment.LEFT} />
              </CalcSelect>
            </FormGroup>
          </div>

          {config.formRows.map((row, idx) => this.renderFormRow(row, idx == 0, idx))}

          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button text="Cancelar" onClick={() => this.onClose()} />
            <Button text="Salvar" intent="primary" onClick={() => this.handleSaveMemoria()} />
          </div>

        </Dialog>

      </>
    );
  }
}

export default MemoriaBase;
